import { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSort, setActiveSort] = useState<'latest' | 'hot'>('latest');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const category = useMemo(() => categories.find((c) => c.slug === slug), [slug]);

  const filteredArticles = useMemo(() => {
    let result = articles.filter((a) => a.category === slug);
    if (activeSort === 'latest') {
      result = [...result].sort((a, b) => new Date(b.publishDate).getTime() - new Date(a.publishDate).getTime());
    } else {
      result = [...result].sort((a, b) => b.readCount - a.readCount);
    }
    return result;
  }, [slug, activeSort]);

  const otherCategories = categories.filter((c) => c.slug !== slug);

  if (!category) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 pb-16">
          <div className="max-w-7xl mx-auto px-4 md:px-6 text-center py-20">
            <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 mb-4">
              <i className="ri-emotion-sad-line text-2xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-bold text-foreground-950 mb-2">分类不存在</h1>
            <p className="text-foreground-500 mb-6">您访问的分类页面不存在，请检查链接是否正确</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              <i className="ri-home-4-line"></i> 返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const pageSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${category.name} - 吃瓜51`,
    description: category.description,
    url: `https://chigua51.com/category/${slug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '吃瓜51',
      url: 'https://chigua51.com',
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(pageSchema)}</script>
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="bg-background-100 border-b border-background-200/70">
            <div className="max-w-7xl mx-auto px-4 md:px-6 py-6 md:py-8">
              <Breadcrumb
                items={[
                  { label: '首页', path: '/' },
                  { label: '分类' },
                  { label: category.name },
                ]}
              />
              <div className="mt-4 flex flex-col sm:flex-row sm:items-center gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-500">
                    <i className={`${category.icon} text-xl text-white`}></i>
                  </div>
                  <div>
                    <h1 className="text-xl md:text-2xl font-bold text-foreground-950" style={{ fontFamily: "var(--font-heading)" }}>
                      {category.name}
                    </h1>
                    <p className="text-sm text-foreground-500 mt-0.5">{category.description}</p>
                  </div>
                </div>
                <div className="sm:ml-auto flex items-center gap-2">
                  <button
                    onClick={() => setActiveSort('latest')}
                    className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                      activeSort === 'latest'
                        ? 'bg-primary-500 text-white'
                        : 'bg-background-50 text-foreground-700 hover:text-foreground-950 border border-background-200/70'
                    }`}
                  >
                    <i className="ri-time-line mr-1.5"></i>最新
                  </button>
                  <button
                    onClick={() => setActiveSort('hot')}
                    className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                      activeSort === 'hot'
                        ? 'bg-primary-500 text-white'
                        : 'bg-background-50 text-foreground-700 hover:text-foreground-950 border border-background-200/70'
                    }`}
                  >
                    <i className="ri-fire-line mr-1.5"></i>最热
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
            <div className="flex flex-col lg:flex-row gap-8">
              <div className="flex-1 min-w-0">
                {filteredArticles.length > 0 ? (
                  <div className="space-y-4">
                    {filteredArticles.map((article, index) => (
                      <ArticleCard
                        key={article.id}
                        variant={index === 0 ? 'featured' : 'default'}
                        slug={article.slug}
                        title={article.title}
                        excerpt={article.excerpt}
                        category={article.category}
                        categoryName={article.categoryName}
                        publishDate={article.publishDate}
                        readCount={article.readCount}
                        commentCount={article.commentCount}
                        imageUrl={article.imageUrl}
                        isHot={article.isHot}
                        isNew={article.isNew}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-20 text-foreground-500">
                    <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 mb-4">
                      <i className="ri-inbox-line text-2xl text-foreground-400"></i>
                    </div>
                    <p>该分类下暂无内容</p>
                  </div>
                )}
              </div>

              <aside className="lg:w-72 flex-shrink-0">
                <div className="sticky top-20 space-y-5">
                  <div className="p-4 rounded-xl bg-background-100 border border-background-200/70">
                    <h3 className="text-sm font-semibold text-foreground-950 mb-3 flex items-center gap-2">
                      <i className="ri-apps-line text-accent-500"></i>
                      其他分类
                    </h3>
                    <div className="space-y-1">
                      {otherCategories.slice(0, 6).map((cat) => (
                        <Link
                          key={cat.slug}
                          to={`/category/${cat.slug}`}
                          className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-foreground-700 hover:text-foreground-950 hover:bg-background-50 transition-colors no-underline"
                        >
                          <div className="w-7 h-7 flex items-center justify-center rounded-md bg-secondary-100 text-secondary-600 flex-shrink-0">
                            <i className={`${cat.icon} text-xs`}></i>
                          </div>
                          <span className="flex-1">{cat.name}</span>
                          <span className="text-xs text-foreground-400">{cat.count}</span>
                        </Link>
                      ))}
                    </div>
                  </div>

                  <div className="p-4 rounded-xl bg-background-100 border border-background-200/70">
                    <h3 className="text-sm font-semibold text-foreground-950 mb-3 flex items-center gap-2">
                      <i className="ri-fire-line text-primary-500"></i>
                      热门标签
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {['恋情曝光', '明星离婚', '偷税漏税', '网络暴力', '人设崩塌', '独家爆料', '综艺剧透', '品牌解约'].map((tag) => (
                        <span
                          key={tag}
                          className="px-2.5 py-1 rounded-full bg-background-50 text-xs text-foreground-600 border border-background-200/70 hover:border-primary-300/50 hover:text-primary-500 transition-colors cursor-pointer whitespace-nowrap"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}